import { m } from 'framer-motion';
// @mui
import { Box, Button, Container, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
import useLocales from '../hooks/useLocales';
// utils
import {
  PlanFeature,
  MIN_TIER,
  TIER_LABELS,
  canUsePlanFeature,
  resolvePlanTier,
} from '../utils/planGating';
// routes
import { PATH_DASHBOARD } from '../routes/paths';
// components
import { MotionContainer, varBounce } from '../components/animate';
import { ForbiddenIllustration } from '../assets';
import { tokens } from '../locales/tokens';

// ----------------------------------------------------------------------

type Props = {
  hasContent?: boolean;
  feature: PlanFeature;
  children: React.ReactNode;
};

export default function PlanBasedGuard({ hasContent = true, feature, children }: Props) {
  const { user } = useAuth();
  const { t } = useLocales();
  const planTier = resolvePlanTier(user?.plan_tier);
  const allowed = canUsePlanFeature(planTier, feature);

  if (allowed) {
    return <>{children}</>;
  }

  if (!hasContent) {
    return null;
  }

  const minTier = MIN_TIER[feature];

  return (
    <Container component={MotionContainer} sx={{ textAlign: 'center', py: 6 }}>
      <m.div variants={varBounce().in}>
        <Typography variant="h3" paragraph>
          {t(tokens.pages.planLock.title[feature])}
        </Typography>
      </m.div>

      <m.div variants={varBounce().in}>
        <Typography sx={{ color: 'text.secondary' }}>
          {t(tokens.pages.planLock.upgradeRequired, { tier: TIER_LABELS[minTier] })}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t(tokens.pages.planLock.upgradeCurrentPlan, { tier: TIER_LABELS[planTier] })}
        </Typography>
      </m.div>

      <m.div variants={varBounce().in}>
        <ForbiddenIllustration sx={{ height: 220, my: { xs: 4, sm: 6 } }} />
      </m.div>

      <Box>
        <Button
          variant="contained"
          component={RouterLink}
          to={PATH_DASHBOARD.organization.subscription}
        >
          {t(tokens.pages.planLock.upgradeCta)}
        </Button>
      </Box>
    </Container>
  );
}
